import React from "react";
import {
  activeFilterKeys,
  selectionSummary,
  type FilterState,
  type MatchMode,
  type TaktAttribute,
} from "../filtering/attributes";
import "./AppliedFilterBar.css";

interface Props {
  attributes: TaktAttribute[];
  filters: FilterState;
  matchMode: MatchMode;
  shownActivities: number;
  totalActivities: number;
  shownSections: number;
  onClearAttr: (key: string) => void;
  onClearAll: () => void;
}

/**
 * The applied-filter bar above the grid: how many sections survive, a removable
 * chip per active attribute, and clear-all. Makes the narrowed state
 * unmistakable so a filtered board never reads as a broken/empty one.
 */
const AppliedFilterBar: React.FC<Props> = ({
  attributes,
  filters,
  matchMode,
  shownActivities,
  totalActivities,
  shownSections,
  onClearAttr,
  onClearAll,
}) => {
  const activeKeys = activeFilterKeys(filters);
  if (activeKeys.length === 0) return null;

  return (
    <div className="takt-filter-bar">
      <span className="takt-filter-bar__count">
        Showing <strong>{shownActivities}</strong> of {totalActivities} activities
        {" · "}
        {shownSections} {shownSections === 1 ? "section" : "sections"}
        {activeKeys.length >= 2 && (
          <span className="takt-filter-bar__mode">
            {" "}
            · matching {matchMode === "all" ? "all" : "any"}
          </span>
        )}
      </span>
      <div className="takt-filter-bar__chips">
        {activeKeys.map((key) => {
          const attr = attributes.find((a) => a.key === key);
          if (!attr) return null;
          const ids = filters[key];
          const text =
            attr.kind === "boolean"
              ? attr.label
              : `${attr.label}: ${selectionSummary(attr, ids)}`;
          return (
            <span className="takt-filter-chip" key={key}>
              {text}
              <button
                type="button"
                className="takt-filter-chip__x"
                title={`Remove ${attr.label} filter`}
                onClick={() => onClearAttr(key)}
              >
                ×
              </button>
            </span>
          );
        })}
      </div>
      <button
        type="button"
        className="takt-filter-bar__clear"
        onClick={onClearAll}
      >
        Clear all
      </button>
    </div>
  );
};

export default AppliedFilterBar;
